import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { attractionBookings } from "../../../../services/attractionBookingService";
import Dialog from "../../../dialog/Dialog";
import Pagination from "../../../common/Pagination";
import Toster from "../../../common/Toster";
import { AdminPageCard, AdminPageCardToolbar } from "../../../common/AdminPageLayout";
import { getTableEmptyProps } from "../../../common/supplierTableEmpty";
import {
  SupplierFilterToolbar,
  SupplierFilterField,
} from "../../../common/supplierPageUi";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../../common/adminTable";
import { getPricingFeeFields } from "../BookingPricingFees";
import {
  formatDate,
  formatTime,
  getPaymentStatusColor,
  getDaysUntilTravel,
  selectedRowClass,
} from "../bookingDetailHelpers";
import {
  BookingDetailHeader,
  BookingDetailSection,
  BookingDetailFields,
  BookingDetailTable,
  BookingDetailMetaChip,
  BookingDetailBadge,
  BookingDetailUserCard,
  BookingDetailNav,
  BookingPaymentProgress,
  CopyableText,
} from "../BookingDetailUi";

const SECTION_IDS = {
  overview: "attraction-booking-overview",
  tickets: "attraction-booking-tickets",
  payment: "attraction-booking-payment",
  customer: "attraction-booking-customer",
};

const NAV_ITEMS = [
  { id: SECTION_IDS.overview, label: "Overview", icon: "fi fi-rr-apps" },
  { id: SECTION_IDS.tickets, label: "Tickets", icon: "fi fi-rr-ticket" },
  { id: SECTION_IDS.payment, label: "Payment", icon: "fi fi-rr-credit-card" },
  { id: SECTION_IDS.customer, label: "Customer", icon: "fi fi-rr-user" },
];

const AttractionBookings = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [activeSection, setActiveSection] = useState(SECTION_IDS.overview);
  const [copyToast, setCopyToast] = useState(null);

  const { data: bookingsData } = useQuery({
    queryKey: ["attractionBookingsSupplier", currentPage, searchQuery],
    queryFn: () => attractionBookings(currentPage, searchQuery),
  });

  const bookingsList = bookingsData?.data?.data || [];
  const pagination = {
    currentPage: bookingsData?.data?.current_page || 1,
    lastPage: bookingsData?.data?.last_page || 1,
    perPage: bookingsData?.data?.per_page || 10,
    total: bookingsData?.data?.total || 0,
    from: bookingsData?.data?.from || 0,
    to: bookingsData?.data?.to || 0,
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const handleViewDetails = (booking) => {
    setSelectedBookingId(booking.id);
    setActiveSection(SECTION_IDS.overview);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedBookingId(null);
    setActiveSection(SECTION_IDS.overview);
  };

  const handleCopied = useCallback((label, type = "success") => {
    setCopyToast({ message: `${label} copied to clipboard`, type });
    setTimeout(() => setCopyToast(null), 2500);
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });

  const getPaymentStatus = (booking) => {
    const balance = parseFloat(booking?.balance || 0);
    const paid = parseFloat(booking?.total_paid || 0);
    if (balance === 0 && paid > 0) return "Paid";
    if (paid > 0) return "Partially Paid";
    return "Unpaid";
  };

  const selectedBooking = bookingsList.find((b) => b.id === selectedBookingId);
  const selectedBookingTickets =
    selectedBooking?.attraction_booking_tickets ||
    selectedBooking?.attractionBookingTickets ||
    [];

  const rawAttractionPayment =
    selectedBooking?.attraction_payment || selectedBooking?.attractionPayment;
  const selectedBookingPayment = Array.isArray(rawAttractionPayment)
    ? rawAttractionPayment[0]
    : rawAttractionPayment;

  const ticketsSubTotal = selectedBookingTickets.reduce((sum, t) => {
    const val = parseFloat(t?.total_price || 0);
    return sum + (Number.isFinite(val) ? val : 0);
  }, 0);

  return (
    <>
      {copyToast && (
        <Toster
          message={copyToast.message}
          type={copyToast.type}
          duration={2500}
          onClose={() => setCopyToast(null)}
        />
      )}

      <AdminPageCard>
        <AdminPageCardToolbar>
          <SupplierFilterToolbar
            showReset={!!searchQuery}
            onReset={() => setSearchQuery("")}
          >
            <SupplierFilterField label="Search">
              <TableSearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search attraction bookings..."
                className="mt-1 w-full sm:w-full"
              />
            </SupplierFilterField>
          </SupplierFilterToolbar>
        </AdminPageCardToolbar>

        <AdminTable className="shadow-none rounded-none">
          <AdminTableHead>
            <AdminTableTh>#</AdminTableTh>
            <AdminTableTh>Attraction</AdminTableTh>
            <AdminTableTh>Visit Date</AdminTableTh>
            <AdminTableTh>Total Amount</AdminTableTh>
            <AdminTableTh>Paid Amount</AdminTableTh>
            <AdminTableTh>Balance</AdminTableTh>
            <AdminTableTh>Status</AdminTableTh>
            <AdminTableTh align="right">Actions</AdminTableTh>
          </AdminTableHead>
          <AdminTableBody>
            {bookingsList.length > 0 ? (
              bookingsList.map((booking, index) => (
                <AdminTableRow
                  key={booking.id}
                  className={selectedRowClass(
                    selectedBookingId === booking.id && showDetailsDialog
                  )}
                >
                  <AdminTableCell>{index + 1}</AdminTableCell>
                  <AdminTableCell>{booking.attraction?.name || "Attraction"}</AdminTableCell>
                  <AdminTableCell>
                    {formatDate(booking.visit_date || booking.created_at)}
                  </AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.total_amount)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.total_paid)}</AdminTableCell>
                  <AdminTableCell>{formatCurrency(booking.balance)}</AdminTableCell>
                  <AdminTableCell>
                    <span
                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ring-1 ring-inset ${getPaymentStatusColor(
                        getPaymentStatus(booking)
                      )}`}
                    >
                      {getPaymentStatus(booking)}
                    </span>
                  </AdminTableCell>
                  <TableActionsCell>
                    <ViewDetailsButton
                      onClick={() => handleViewDetails(booking)}
                      ariaLabel={`View details for booking ${booking.id}`}
                    />
                  </TableActionsCell>
                </AdminTableRow>
              ))
            ) : (
              <TableEmptyRow
                colSpan={8}
                {...getTableEmptyProps("attractionBookings", {
                  hasActiveFilters: !!searchQuery,
                  onClearFilters: () => setSearchQuery(""),
                })}
              />
            )}
          </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {showDetailsDialog && selectedBooking && (
        <Dialog
          title="Attraction Booking Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-full"
          variant="detail"
          showCloseButton={false}
        >
          <div className="space-y-5">
            <BookingDetailNav items={NAV_ITEMS} activeId={activeSection} onSelect={scrollToSection} />

            <BookingDetailHeader
              bookingId={selectedBooking.id}
              title={selectedBooking.attraction?.name || "Attraction"}
              onCopyBookingId={handleCopied}
              meta={
                <>
                  <BookingDetailMetaChip icon="fi fi-rr-calendar">
                    Visit: {formatDate(selectedBooking.visit_date)}
                  </BookingDetailMetaChip>
                  {getDaysUntilTravel(selectedBooking.visit_date) ? (
                    <BookingDetailMetaChip icon="fi fi-rr-clock">
                      {getDaysUntilTravel(selectedBooking.visit_date)}
                    </BookingDetailMetaChip>
                  ) : null}
                  <BookingDetailMetaChip icon="fi fi-rr-marker">
                    {selectedBooking.attraction?.location || "Location N/A"}
                  </BookingDetailMetaChip>
                </>
              }
              status={getPaymentStatus(selectedBooking)}
              statusClassName={getPaymentStatusColor(getPaymentStatus(selectedBooking))}
              amount={formatCurrency(selectedBooking.total_amount)}
              bookedAt={formatDate(selectedBooking.created_at)}
              bookedTime={formatTime(selectedBooking.created_at)}
              stats={[
                { label: "Paid", value: formatCurrency(selectedBooking.total_paid) },
                {
                  label: "Balance",
                  value: formatCurrency(selectedBooking.balance),
                  highlight: parseFloat(selectedBooking.balance) > 0,
                },
                { label: "Tickets", value: formatCurrency(ticketsSubTotal), icon: "fi fi-rr-ticket" },
                {
                  label: "Items",
                  value: selectedBookingTickets.length,
                  icon: "fi fi-rr-box",
                },
              ]}
            />

            <BookingPaymentProgress
              total={selectedBooking.total_amount}
              paid={selectedBooking.total_paid}
              balance={selectedBooking.balance}
              formatCurrency={formatCurrency}
            />

            <div id={SECTION_IDS.overview} className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <BookingDetailSection title="Booking Information" icon="fi fi-rr-receipt">
                <BookingDetailFields
                  onCopied={handleCopied}
                  fields={[
                    {
                      label: "Booking ID",
                      value: `#${selectedBooking.id}`,
                      copyable: true,
                      copyValue: selectedBooking.id,
                    },
                    { label: "Visit Date", value: formatDate(selectedBooking.visit_date) },
                    {
                      label: "Total Amount",
                      value: formatCurrency(selectedBooking.total_amount),
                      highlight: true,
                    },
                    { label: "Discount", value: formatCurrency(selectedBooking.discount_amount) },
                    ...getPricingFeeFields(selectedBooking, formatCurrency),
                    {
                      label: "Paid Amount",
                      value: formatCurrency(selectedBooking.total_paid),
                      highlight: parseFloat(selectedBooking.balance) === 0,
                    },
                    { label: "Balance", value: formatCurrency(selectedBooking.balance) },
                  ]}
                />
              </BookingDetailSection>

              <BookingDetailSection title="Attraction Details" icon="fi fi-rr-mountain">
                <BookingDetailFields
                  fields={[
                    {
                      label: "Attraction Name",
                      value: selectedBooking.attraction?.name || "—",
                      fullWidth: true,
                    },
                    {
                      label: "Location",
                      value: selectedBooking.attraction?.location || "Not specified",
                      fullWidth: true,
                    },
                    { label: "Attraction ID", value: selectedBooking.attraction?.id || "—" },
                  ]}
                />
              </BookingDetailSection>
            </div>

            <BookingDetailSection
              id={SECTION_IDS.tickets}
              title="Tickets"
              icon="fi fi-rr-ticket"
              action={
                selectedBookingTickets.length > 0 ? (
                  <span className="text-[10px] font-medium text-gray-400">
                    {selectedBookingTickets.length} item
                    {selectedBookingTickets.length === 1 ? "" : "s"}
                  </span>
                ) : null
              }
            >
              <BookingDetailTable
                columns={["Ticket type", "Qty", "Unit price", "Total"]}
                emptyTitle="No tickets"
                emptyMessage="Ticket line items are not available for this booking."
                rows={selectedBookingTickets.map((t, idx) => (
                  <tr key={t.id || idx} className="transition hover:bg-gray-50/80">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {t?.attraction_ticket_type?.attraction_ticket_type_master?.name || "Ticket"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {t.quantity ?? 0}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                      {formatCurrency(t.unit_price)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                      {formatCurrency(t.total_price)}
                    </td>
                  </tr>
                ))}
              />
            </BookingDetailSection>

            <BookingDetailSection id={SECTION_IDS.payment} title="Payment Details" icon="fi fi-rr-credit-card">
              <BookingDetailFields
                onCopied={handleCopied}
                fields={[
                  {
                    label: "Status",
                    value: selectedBookingPayment?.status ? (
                      <BookingDetailBadge
                        className={
                          selectedBookingPayment.status === "completed"
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            : "bg-amber-50 text-amber-700 ring-amber-600/20"
                        }
                      >
                        {selectedBookingPayment.status}
                      </BookingDetailBadge>
                    ) : (
                      "—"
                    ),
                  },
                  {
                    label: "Order ID",
                    value: selectedBookingPayment?.order_id ? (
                      <CopyableText
                        value={selectedBookingPayment.order_id}
                        label="Order ID"
                        onCopied={handleCopied}
                        mono
                        className="font-mono text-xs"
                      />
                    ) : (
                      "—"
                    ),
                  },
                  {
                    label: "Payment ID",
                    value:
                      selectedBookingPayment?.payment_id ||
                      selectedBookingPayment?.razorpay_payment_id ||
                      "—",
                    copyable: Boolean(
                      selectedBookingPayment?.payment_id ||
                        selectedBookingPayment?.razorpay_payment_id
                    ),
                    copyValue:
                      selectedBookingPayment?.payment_id ||
                      selectedBookingPayment?.razorpay_payment_id,
                  },
                  {
                    label: "Method",
                    value:
                      selectedBookingPayment?.payment_method ||
                      selectedBookingPayment?.payment_type ||
                      "—",
                  },
                ]}
                columns={2}
              />
            </BookingDetailSection>

            <BookingDetailSection id={SECTION_IDS.customer} title="User Details" icon="fi fi-rr-user">
              <BookingDetailUserCard user={selectedBooking.user} onCopied={handleCopied} />
            </BookingDetailSection>
          </div>
        </Dialog>
      )}

      <div className="mt-4">
        <Pagination pagination={pagination} onPageChange={handlePageChange} showInfo={true} />
      </div>
    </>
  );
};

export default AttractionBookings;
